// ═══════════════════════════════════════════════════════════════════════════
// 🧠 MEMORY CACHE: Cache em Memória com TTL
// Para dados que mudam frequentemente e precisam expirar
// ═══════════════════════════════════════════════════════════════════════════

export class MemoryCache {
  constructor() {
    this.cache = new Map();
    this.timers = new Map();
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      deletes: 0
    };
  }

  // Armazenar valor com TTL
  set(key, value, ttlMs = 1800000) {
    // Limpar timer antigo se existir
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
    }

    // Armazenar
    this.cache.set(key, value);
    this.stats.sets++;

    // Criar timer de expiração
    const timer = setTimeout(() => {
      this.delete(key);
    }, ttlMs);

    this.timers.set(key, timer);
    console.log(`💾 Memory cache SET: ${key} (TTL: ${ttlMs / 1000}s)`);
  }

  // Recuperar valor
  get(key) {
    if (this.cache.has(key)) {
      this.stats.hits++;
      console.log(`✅ Memory cache HIT: ${key}`);
      return this.cache.get(key);
    }
    this.stats.misses++;
    console.log(`🔄 Memory cache MISS: ${key}`);
    return undefined;
  }

  // Deletar valor
  delete(key) {
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
    }
    this.cache.delete(key);
    this.stats.deletes++;
    console.log(`🗑️  Memory cache DELETE: ${key}`);
  }

  // Verificar existência
  has(key) {
    return this.cache.has(key);
  }

  // Limpar tudo
  clear() {
    this.timers.forEach(timer => clearTimeout(timer));
    this.cache.clear();
    this.timers.clear();
    console.log("🗑️  Memory cache CLEAR: all entries deleted");
  }

  // Obter tamanho
  size() {
    return this.cache.size;
  }

  // Obter estatísticas
  getStats() {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? ((this.stats.hits / total) * 100).toFixed(1) : 0;
    return {
      ...this.stats,
      hitRate: hitRate + "%",
      totalEntries: this.cache.size
    };
  }

  // Reset estatísticas
  resetStats() {
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      deletes: 0
    };
    console.log("📊 Memory cache stats reset");
  }

  // Obter todas as chaves
  keys() {
    return Array.from(this.cache.keys());
  }

  // Obter entrada com metadados
  getEntry(key) {
    if (!this.cache.has(key)) return null;
    return {
      key,
      value: this.cache.get(key),
      size: JSON.stringify(this.cache.get(key)).length
    };
  }

  // Listar todas as entradas
  listAll() {
    const entries = [];
    this.cache.forEach((value, key) => {
      entries.push({
        key,
        size: JSON.stringify(value).length
      });
    });
    return entries;
  }
}

// Instância global
export const memCache = new MemoryCache();

// Funções helper
export const setMemCache = (key, value, ttl = 1800000) => memCache.set(key, value, ttl);
export const getMemCache = (key) => memCache.get(key);
export const deleteMemCache = (key) => memCache.delete(key);
export const clearMemCache = () => memCache.clear();
export const getMemCacheStats = () => memCache.getStats();
